import React from "react";
import Link from "components/link";
import Loading from "components/loading";
import styles from "./footer.module.scss"
import query from "./query.js";
import {ReactComponent as WhiteLogo} from '../../../src/assets/fh_logo_white.svg';
import {ReactComponent as FacebookLogo} from '../../../src/assets/facebook.svg';
import {ReactComponent as IGLogo} from '../../../src/assets/instagram.svg';
import {ReactComponent as TwitterLogo} from '../../../src/assets/twitter.svg';
import {ReactComponent as LinkedInLogo} from '../../../src/assets/linkedIn.svg';
import useIsMobile from "helpers/useIsMobile";

import { useQuery } from "@apollo/react-hooks";

const Footer = () => {
   
    const { loading, data, error } = useQuery(query);
    
    // breakpoint matches media breakpoint in footer.module.scss
    const breakpoint = 992;
    const isMobile = useIsMobile(breakpoint);

    /* 
        Pardot email forms (which the client uses) are weird, they don't accept AJAX requests and instead send back 
        success or failure URLs to redirect the user to either a success or failure location. To get around re-routing the user to a new page 
        every time they subscribe to the email list, the email form handler is embedded in an iframe in the footer. The success / failure pages are simple 
        text pages which are then served back in that iframe, simulating a success or failure message.

    */
    const formHtml = `
        <!DOCTYPE html>
        <html>
        <head>
        <link rel="stylesheet" href="https://use.typekit.net/zwr6ikj.css">
            <meta charset=&quot;utf-8&quot;>
            <style type="text/css"> 

            body {
                margin: 0px;
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
            }

            .newsLetterTitle {
                color: #FFFFFF;
                margin: 0px;
                font-size: 16px;
                line-height: 26px;
                font-family: "adelle-sans", sans-serif;
            }

            .newsLetterEmail {
                box-sizing: border-box;
                background-color: transparent;
                color: #FFFFFF;
                border: 1.25px solid #FFFFFF;
                border-radius: 0;
                height: 48px;
                width: 63%;
                padding: 0px 0px 0px 16px;
                font-size: 16px;
                line-height: 26px;
                font-family: "adelle-sans", sans-serif;
            }

            .newsLetterEmail:focus {
                outline-offset: 10px;
            }

            .newsLetterEmail:focus-visible {
                outline-color: #ffffff;
                outline-offset: 12px;
                outline-style: solid;
                outline-width: 1px;
            }

            .newsLetterForm {
                flex-direction: column;
                display: flex;
                padding-top: 12px;

            }
            .newsLetterSubmit {
                cursor: pointer;
                background-color: transparent;
                color: #FFFFFF; 
                height: 48px;
                width: 112px;
                border: 1.25px solid #FFFFFF;
                padding: 0px;
                margin-top: 12px;
                font-size: 16px;
                line-height: 26px;
                font-family: "adelle-sans", sans-serif;
                transition: background-color .3s;   
            }

            .newsLetterSubmit:hover {
                background-color: #FFFFFF;
                color: #172663;
            }

            .newsLetterSubmit:focus {
                outline-offset: 10px;
            }

            .newsLetterSubmit:focus-visible {
                outline-color: #ffffff;
                outline-offset: 12px;
                outline-style: solid;
                outline-width: 1px;
            }

            @media (min-width: 289px) {
                .newsLetterForm {
                    flex-direction: row;
                }   

                .newsLetterSubmit {
                    margin-top: 0px;
                    margin-left: 16px;
                    
                }
            }
            </style>
        </head>
        <body>
        <p class="newsLetterTitle">Join Our Newsletter:</p>
        <form 
            class="newsLetterForm" 
            action="https://go.fountainhouse.org/l/600121/2020-12-17/25xhmy" 
            method="post" 
            encType=""
            target="_self"
        >
            <input type="email" name="email" class="newsLetterEmail" placeholder="Email Address" required />
            <button aria-label="submit" class="newsLetterSubmit">SUBMIT</button>
        </form>
        </body>
        </html>
    `;

    if (loading) {
        return <Loading />;
    }

    if (error) {
        console.log('error: ', error);
		return null;
    }

    const {officeName, streetAddress, cityStateZipcode, logoImageBlock } = data.globalSet;
    const {facebook, instagram, youtube, twitter} = data.globalSet.socialMediaUrls[0];

    return (
        <footer>
            <div className={styles.footerLinks}>
                {data.globalSet.footerLinks.map( ({id, linkTo}) => {
                    return (
                        <Link key={id} className={styles.linkItem} {...linkTo}/> 
                    ); 
                })}
            </div>
            <div className={styles.footerBottom}>

                <div className={styles.footerSection}>
                    <WhiteLogo className={styles.footerLogo}/>
                    { (officeName || cityStateZipcode) &&
                        <div className={styles.contactInfo}>
                            <p className={styles.officeName}>{officeName}</p>
                            <div className={styles.addressBlock}>
                                <p>{streetAddress}</p>
                                <p>{cityStateZipcode}</p>
                            </div>

                        </div>
                    }
                </div>
                <div className={styles.newsLetterSection}>
                    <iframe 
                        srcDoc={formHtml}
                        title="Email Signup Form Field" 
                        name="Email Signup Form Field" 
                        className={styles.newsLetterOutputFrame} 
                    >
                    </iframe>

                </div>
                { logoImageBlock && logoImageBlock.length > 0 &&
                    <div className={styles.donateLogoSection}>
                        {
                            logoImageBlock.map( (item) => {
                                if (item.logo.length === 0) {
                                    return null;
                                }

                                if (item.externalLink.url) {
                                    return <Link key={item.id} {...item.externalLink} title={`Go to ${item.externalLink.url}`}> 
                                        <img
                                            alt={item.altText} 
                                            src={item.logo[0].url}
                                        />      
                                    </Link>
                                }

                                return (
                                    <img
                                        key={item.id}
                                        alt={item.altText} 
                                        src={item.logo[0].url}
                                    />
                                );
                            })
                        }

                    </div>
                }
                {
                    isMobile ? 
                        <div className={styles.mediaGrid}>
                            <Link url={twitter.url} title="Go to FH's Twitter"><TwitterLogo alt="Twitter Logo"/></Link>
                            <Link url={instagram.url} title="Go to FH's Instagram"><IGLogo alt="Instagram Logo"/></Link>
                            <Link url={facebook.url} title="Go to FH's Facebook"><FacebookLogo alt="Facebook Logo"/></Link>
                            <Link url={youtube.url} title="Go to FH's LinkedIn"><LinkedInLogo alt="LinkedIn Logo"/></Link>
                        </div>
                    :
                        <div className={styles.mediaLinks}>
                            <div className={styles.mediaRow}>
                                <Link url={twitter.url} title="Go to FH's Twitter"><TwitterLogo alt="Twitter Logo"/></Link>
                                <Link url={instagram.url} title="Go to FH's Instagram"><IGLogo alt="Instagram Logo"/></Link>
                            </div>
                            <div className={styles.mediaRow}>
                                <Link url={facebook.url} title="Go to FH's Facebook"><FacebookLogo alt="Facebook Logo"/></Link>
                                <Link url={youtube.url} title="Go to FH's LinkedIn"><LinkedInLogo alt="LinkedIn Logo"/></Link>
                            </div>
                        </div>
                }
            </div>
        </footer>
    );
}

export default Footer